import { useState } from 'react';
import {
  Chip, IconButton, Tooltip, Stack, TextField, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, Box, Alert, Snackbar,
} from '@mui/material';
import { Visibility, MoreVert, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { projectOnboardingApi, OPERATIONAL_STATUSES, type ProjectApplication, type ProjectReviewDecision } from '../../../api/projectOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const DECISIONS: ProjectReviewDecision[] = ['approve', 'reject', 'send_back', 'request_clarification'];
const DECISION_LABEL: Record<ProjectReviewDecision, string> = { approve: 'Approve', reject: 'Reject', send_back: 'Send Back for Correction', request_clarification: 'Request Clarification' };
const DECISION_COLOR: Record<ProjectReviewDecision, 'success' | 'error' | 'warning' | 'info'> = { approve: 'success', reject: 'error', send_back: 'warning', request_clarification: 'info' };
const statusColor = (s: string): 'default' | 'success' | 'error' | 'warning' | 'info' =>
  ['approved', 'active'].includes(s) ? 'success' : s === 'rejected' ? 'error' : s === 'closed' ? 'default'
    : ['clarification_required', 'sent_back'].includes(s) ? 'warning' : 'info';
const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const PENDING = ['submitted', 'under_review', 'clarification_required'];

export function ProjectApplicationsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [statusFor, setStatusFor] = useState<ProjectApplication | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = hasPermission('project_onboarding.create');
  const canReview = hasPermission('project_onboarding.review');
  const canSubmit = hasPermission('project_onboarding.submit');
  const canStatus = hasPermission('project_onboarding.change_status');

  const { data, isLoading } = useQuery({ queryKey: ['project-applications', page, limit, status], queryFn: () => projectOnboardingApi.list({ page, limit, status: status || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['project-applications'] });
  const run = (p: Promise<unknown>, msg: string) => p.then(() => { setToast(msg); refresh(); }).catch((e) => setToast(extractApiError(e)));
  const newApp = () => projectOnboardingApi.create({ project_name: 'New Project' }).then((a) => { refresh(); navigate(`/project-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)));

  const columns: Column<ProjectApplication>[] = [
    { key: 'application_code', label: 'Code', width: 150 },
    { key: 'project_name', label: 'Project' },
    { key: 'client_name', label: 'Client', render: (r) => r.client_name ?? '—' },
    { key: 'project_type', label: 'Type', render: (r) => human(r.project_type) },
    { key: 'project_value', label: 'Value', render: (r) => (r.project_value != null ? Number(r.project_value).toLocaleString('en-IN') : '—') },
    { key: 'source_type', label: 'Source', render: (r) => r.source_type ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor(r.status)} label={human(r.status)} /> },
    { key: 'project_status', label: 'Lifecycle', render: (r) => (r.project_id ? <Chip size="small" variant="outlined" label={human(r.project_status)} /> : '—') },
  ];

  return (
    <>
      <PageHeader title="Project Applications" subtitle="Project onboarding — Draft → Submitted → Under Review → Approved → Active → Closed."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={newApp}>New Project</Button>} />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {['draft', 'sent_back', 'under_review', 'clarification_required', 'approved', 'active', 'closed', 'rejected'].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <RowActions row={r} perms={{ canReview, canSubmit, canStatus, canCreate }}
            onView={() => setReviewId(r.id)}
            onEdit={() => navigate(`/project-applications/${r.id}/edit`)}
            onSubmit={() => run(projectOnboardingApi.submit(r.id), 'Submitted for review')}
            onWithdraw={() => run(projectOnboardingApi.withdraw(r.id), 'Application withdrawn')}
            onDelete={() => run(projectOnboardingApi.remove(r.id), 'Draft deleted')}
            onReopen={() => run(projectOnboardingApi.reopen(r.id), 'Application reopened')}
            onClone={() => projectOnboardingApi.clone(r.id).then((a) => { setToast('Cloned to a new draft'); refresh(); navigate(`/project-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
            onChangeStatus={() => setStatusFor(r)}
          />
        )} />

      {reviewId && <ReviewDialog id={reviewId} canReview={canReview} onClose={() => setReviewId(null)} />}
      {statusFor && <StatusDialog app={statusFor} onClose={() => setStatusFor(null)} onDone={() => { setStatusFor(null); setToast('Project status updated'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

interface Perms { canReview: boolean; canSubmit: boolean; canStatus: boolean; canCreate: boolean }

function RowActions({ row, perms, onView, onEdit, onSubmit, onWithdraw, onDelete, onReopen, onClone, onChangeStatus }: {
  row: ProjectApplication; perms: Perms;
  onView: () => void; onEdit: () => void; onSubmit: () => void; onWithdraw: () => void; onDelete: () => void; onReopen: () => void; onClone: () => void; onChangeStatus: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const s = row.status;
  const isDraft = s === 'draft' || s === 'sent_back' || s === 'clarification_required';
  const isPending = PENDING.includes(s);
  const items: Array<{ label: string; onClick: () => void; color?: string }> = [];
  if (isDraft && perms.canSubmit) { items.push({ label: 'Edit', onClick: onEdit }); if (s !== 'clarification_required') items.push({ label: 'Submit', onClick: onSubmit }); items.push({ label: 'Delete', onClick: onDelete, color: 'error' }); }
  if (isPending) { if (perms.canReview) items.push({ label: 'Review', onClick: onView }); if (perms.canSubmit) items.push({ label: 'Withdraw', onClick: onWithdraw, color: 'warning' }); }
  if ((s === 'active' || s === 'approved') && perms.canStatus) items.push({ label: 'Change Status', onClick: onChangeStatus });
  if (s === 'rejected') { if (perms.canReview) items.push({ label: 'Reopen', onClick: onReopen }); if (perms.canCreate) items.push({ label: 'Clone', onClick: onClone }); }

  return (
    <>
      <Tooltip title="View"><IconButton size="small" onClick={onView}><Visibility fontSize="small" /></IconButton></Tooltip>
      {items.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            {items.map((it) => <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.color ? { color: `${it.color}.main` } : undefined}>{it.label}</MenuItem>)}
          </Menu>
        </>
      )}
    </>
  );
}

function ReviewDialog({ id, canReview, onClose }: { id: number; canReview: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: app } = useQuery({ queryKey: ['project-application', id], queryFn: () => projectOnboardingApi.get(id) });

  const reviewMut = useMutation({
    mutationFn: (decision: ProjectReviewDecision) => projectOnboardingApi.review(id, decision, remarks || undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-applications'] }); qc.invalidateQueries({ queryKey: ['project-application', id] }); setRemarks(''); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });
  const isPending = app ? PENDING.includes(app.status) : false;
  const terminal = app ? ['approved', 'active', 'rejected', 'closed'].includes(app.status) : false;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{app ? `${app.application_code} — ${app.project_name}` : 'Loading…'}</DialogTitle>
      <DialogContent dividers>
        {app && (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Client" value={app.client_name} />
              <Field label="Type" value={human(app.project_type)} />
              <Field label="Value" value={app.project_value != null ? Number(app.project_value).toLocaleString('en-IN') : null} />
              <Field label="Billing" value={human(app.billing_type)} />
              <Field label="Region/State" value={`${app.region_id ?? '—'} / ${app.state_id ?? '—'}`} />
              <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" color={statusColor(app.status)} label={human(app.status)} /></Box></Box>
            </Stack>
            <Field label="Scope" value={(app.scope_items ?? []).map(human).join(', ') || null} />

            <Divider />
            <Typography variant="subtitle2">Approval workflow</Typography>
            <Stack spacing={0.5}>
              {(app.workflow ?? []).map((w) => (
                <Stack key={w.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`${w.stage_order}. ${human(w.stage)}`} variant="outlined" />
                  <Chip size="small" color={statusColor(w.status)} label={human(w.status)} />
                  {w.remarks && <Typography variant="caption" color="text.secondary">— {w.remarks}</Typography>}
                </Stack>
              ))}
            </Stack>

            {canReview && !terminal && isPending && (
              <>
                <Divider />
                <Typography variant="subtitle2">Action — {human(app.current_stage)} stage</Typography>
                <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline minRows={2} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {DECISIONS.map((d) => <Button key={d} variant="contained" color={DECISION_COLOR[d]} disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(d)}>{DECISION_LABEL[d]}</Button>)}
                </Stack>
              </>
            )}
            {terminal && <Alert severity={app.status === 'rejected' ? 'error' : 'success'}>This application is {human(app.status)}{app.project_id ? ` — project master #${app.project_id} created.` : '.'}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function StatusDialog({ app, onClose, onDone }: { app: ProjectApplication; onClose: () => void; onDone: () => void }) {
  const [statusVal, setStatusVal] = useState(app.project_status || 'active');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({ mutationFn: () => projectOnboardingApi.changeStatus(app.id, statusVal), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Change Project Status — {app.application_code}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Status" value={statusVal} onChange={(e) => setStatusVal(e.target.value)}>
            {OPERATIONAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Update</Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography>{value ?? '—'}</Typography></Box>;
}
